import { BlockId } from '@shared/block-ids';
import { ItemStack, ToolType } from '../types';

export interface CraftingRecipe {
  pattern: (BlockId | null)[][];
  result: ItemStack;
}

// S = stick placeholder: we use PLANKS_OAK in patterns where sticks would go
// In real Minecraft you'd craft sticks from planks first, but we simplify

export const RECIPES: CraftingRecipe[] = [
  // Wood -> Planks (4)
  {
    pattern: [[BlockId.WOOD_OAK]],
    result: { id: BlockId.PLANKS_OAK, count: 4 },
  },
  // Birch -> Planks
  {
    pattern: [[BlockId.WOOD_BIRCH]],
    result: { id: BlockId.PLANKS_OAK, count: 4 },
  },
  // Planks -> Crafting Table
  {
    pattern: [
      [BlockId.PLANKS_OAK, BlockId.PLANKS_OAK],
      [BlockId.PLANKS_OAK, BlockId.PLANKS_OAK],
    ],
    result: { id: BlockId.CRAFTING_TABLE, count: 1 },
  },
  // Cobblestone -> Furnace
  {
    pattern: [
      [BlockId.COBBLESTONE, BlockId.COBBLESTONE, BlockId.COBBLESTONE],
      [BlockId.COBBLESTONE, null, BlockId.COBBLESTONE],
      [BlockId.COBBLESTONE, BlockId.COBBLESTONE, BlockId.COBBLESTONE],
    ],
    result: { id: BlockId.FURNACE, count: 1 },
  },
  // Sand -> Glass
  {
    pattern: [
      [BlockId.SAND, BlockId.SAND],
      [BlockId.SAND, BlockId.SAND],
    ],
    result: { id: BlockId.GLASS, count: 4 },
  },
  // Sandstone
  {
    pattern: [
      [BlockId.SAND, BlockId.SAND],
    ],
    result: { id: BlockId.SANDSTONE, count: 1 },
  },
  // Stone Bricks
  {
    pattern: [
      [BlockId.COBBLESTONE, BlockId.COBBLESTONE],
      [BlockId.COBBLESTONE, BlockId.COBBLESTONE],
    ],
    result: { id: BlockId.BRICK, count: 4 },
  },

  // ===== TOOLS =====
  // Wooden Pickaxe
  {
    pattern: [
      [BlockId.PLANKS_OAK, BlockId.PLANKS_OAK, BlockId.PLANKS_OAK],
      [null, BlockId.WOOD_OAK, null],
      [null, BlockId.WOOD_OAK, null],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.WOOD_PICKAXE, durability: 59 },
  },
  // Wooden Axe
  {
    pattern: [
      [BlockId.PLANKS_OAK, BlockId.PLANKS_OAK],
      [BlockId.PLANKS_OAK, BlockId.WOOD_OAK],
      [null, BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.WOOD_AXE, durability: 59 },
  },
  // Wooden Shovel
  {
    pattern: [
      [BlockId.PLANKS_OAK],
      [BlockId.WOOD_OAK],
      [BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.WOOD_SHOVEL, durability: 59 },
  },
  // Wooden Sword
  {
    pattern: [
      [BlockId.PLANKS_OAK],
      [BlockId.PLANKS_OAK],
      [BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.WOOD_SWORD, durability: 59 },
  },
  // Stone Pickaxe
  {
    pattern: [
      [BlockId.COBBLESTONE, BlockId.COBBLESTONE, BlockId.COBBLESTONE],
      [null, BlockId.WOOD_OAK, null],
      [null, BlockId.WOOD_OAK, null],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.STONE_PICKAXE, durability: 131 },
  },
  // Stone Axe
  {
    pattern: [
      [BlockId.COBBLESTONE, BlockId.COBBLESTONE],
      [BlockId.COBBLESTONE, BlockId.WOOD_OAK],
      [null, BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.STONE_AXE, durability: 131 },
  },
  // Stone Shovel
  {
    pattern: [
      [BlockId.COBBLESTONE],
      [BlockId.WOOD_OAK],
      [BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.STONE_SHOVEL, durability: 131 },
  },
  // Stone Sword
  {
    pattern: [
      [BlockId.COBBLESTONE],
      [BlockId.COBBLESTONE],
      [BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.STONE_SWORD, durability: 131 },
  },
  // Iron Pickaxe
  {
    pattern: [
      [BlockId.IRON_ORE, BlockId.IRON_ORE, BlockId.IRON_ORE],
      [null, BlockId.WOOD_OAK, null],
      [null, BlockId.WOOD_OAK, null],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.IRON_PICKAXE, durability: 250 },
  },
  // Iron Sword
  {
    pattern: [
      [BlockId.IRON_ORE],
      [BlockId.IRON_ORE],
      [BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.IRON_SWORD, durability: 250 },
  },
  // Diamond Pickaxe
  {
    pattern: [
      [BlockId.DIAMOND_ORE, BlockId.DIAMOND_ORE, BlockId.DIAMOND_ORE],
      [null, BlockId.WOOD_OAK, null],
      [null, BlockId.WOOD_OAK, null],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.DIAMOND_PICKAXE, durability: 1561 },
  },
  // Diamond Sword
  {
    pattern: [
      [BlockId.DIAMOND_ORE],
      [BlockId.DIAMOND_ORE],
      [BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.AIR, count: 1, toolId: ToolType.DIAMOND_SWORD, durability: 1561 },
  },
  // Torch
  {
    pattern: [
      [BlockId.COAL_ORE],
      [BlockId.WOOD_OAK],
    ],
    result: { id: BlockId.TORCH, count: 4 },
  },
];

export function findRecipe(grid: (BlockId | null)[][]): CraftingRecipe | null {
  for (const recipe of RECIPES) {
    if (matchesPattern(grid, recipe.pattern)) {
      return recipe;
    }
  }
  return null;
}

function matchesPattern(grid: (BlockId | null)[][], pattern: (BlockId | null)[][]): boolean {
  const pH = pattern.length;
  const pW = pattern[0].length;
  const gH = grid.length;
  const gW = grid[0]?.length ?? 0;

  for (let oy = 0; oy <= gH - pH; oy++) {
    for (let ox = 0; ox <= gW - pW; ox++) {
      if (matchAt(grid, pattern, ox, oy, gW, gH, pW, pH)) {
        return true;
      }
    }
  }
  return false;
}

function matchAt(
  grid: (BlockId | null)[][],
  pattern: (BlockId | null)[][],
  ox: number, oy: number,
  gW: number, gH: number,
  pW: number, pH: number,
): boolean {
  // Check pattern matches at offset
  for (let y = 0; y < pH; y++) {
    for (let x = 0; x < pW; x++) {
      if (pattern[y][x] !== (grid[oy + y]?.[ox + x] ?? null)) {
        return false;
      }
    }
  }

  // Check rest of grid is empty
  for (let y = 0; y < gH; y++) {
    for (let x = 0; x < gW; x++) {
      if (y >= oy && y < oy + pH && x >= ox && x < ox + pW) continue;
      if (grid[y]?.[x] != null) return false;
    }
  }

  return true;
}
